import {commitMutation, graphql} from 'react-relay'
import type {Environment, RecordProxy, RecordSourceSelectorProxy} from 'relay-runtime'

import {forEachMatchingPendingSuggestion, type SuggestionRef, toSuggestionInput} from '../utils/suggestion-ref'
import type {
  applyPendingIssueSuggestionsMutation,
  applyPendingIssueSuggestionsMutation$data,
} from './__generated__/applyPendingIssueSuggestionsMutation.graphql'

export type ApplySuggestionRef = SuggestionRef

export function commitApplyPendingIssueSuggestionsMutation({
  environment,
  issueId,
  actorId,
  suggestions,
  onError,
  onCompleted,
}: {
  environment: Environment
  issueId: string
  actorId: string
  suggestions: ApplySuggestionRef[]
  onError?: (error: Error) => void
  onCompleted?: (response: applyPendingIssueSuggestionsMutation$data) => void
}) {
  return commitMutation<applyPendingIssueSuggestionsMutation>(environment, {
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    mutation: graphql`
      mutation applyPendingIssueSuggestionsMutation($input: ApplyPendingIssueSuggestionsInput!) {
        applyPendingIssueSuggestions(input: $input) {
          clientMutationId
          # Return the updated assignees so applying an ASSIGNEE suggestion reflects the
          # newly-assigned actor (and the server-side auto-assigned viewer for coding agents)
          # in the sidebar without waiting on a separate refetch or subscription.
          issue {
            id
            ...AssigneesSectionAssignees
          }
        }
      }
    `,
    variables: {
      input: {
        issueId,
        actorId,
        suggestions: suggestions.map(toSuggestionInput),
      },
    },
    optimisticUpdater: store => applyOptimisticUpdate(store, issueId, actorId, suggestions),
    updater: store => applyOptimisticUpdate(store, issueId, actorId, suggestions),
    onError: error => onError?.(error),
    onCompleted: response => onCompleted?.(response),
  })
}

function applyOptimisticUpdate(
  store: RecordSourceSelectorProxy,
  issueId: string,
  actorId: string,
  suggestions: ApplySuggestionRef[],
) {
  const issue = store.get(issueId)
  if (!issue) return

  const pendingSuggestions = issue.getLinkedRecords('pendingSuggestions') ?? []

  // The approver (current viewer). Assigning a coding-agent bot auto-assigns
  // them server-side, and approves any pending suggestion to assign them too.
  const viewerId = (
    store.getRoot().getLinkedRecord('safeViewer') ?? store.getRoot().getLinkedRecord('viewer')
  )?.getValue('id') as string | undefined

  for (const suggestion of suggestions) {
    // Mark the matching pending suggestion as APPLIED so the row hides.
    forEachMatchingPendingSuggestion(pendingSuggestions, actorId, suggestion, matched => {
      // Relay stores fields under the schema name (`state`), not the fragment alias.
      matched.setValue('APPLIED', 'state')

      // Applying a coding-agent bot assignment auto-assigns the approver, so
      // mirror the server and approve any pending suggestion to assign them.
      if (suggestion.kind === 'ASSIGNEE' && viewerId) {
        const assignee = matched.getLinkedRecord('assignee')
        if (assignee?.getValue('__typename') === 'Bot' && assignee.getValue('isAgent') === true) {
          markPendingAssigneeSuggestionApplied(pendingSuggestions, viewerId)
        }
      }
    })
  }
}

// Marks any pending assignee suggestion targeting `assigneeId` as APPLIED,
// regardless of which actor suggested it
function markPendingAssigneeSuggestionApplied(
  pendingSuggestions: ReadonlyArray<RecordProxy | null>,
  assigneeId: string,
) {
  for (const s of pendingSuggestions) {
    if (!s) continue
    if (s.getValue('__typename') !== 'PendingAssigneeSuggestion') continue
    const assignee = s.getLinkedRecord('assignee')
    if (assignee?.getValue('id') === assigneeId) {
      s.setValue('APPLIED', 'state')
    }
  }
}
